import React, { useState, useEffect, useMemo } from "react";
import { Plus, LayoutGrid, Pencil, Trash2 } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import DataTable from "../../components/DataTable";
import BulkImportExport from "../../components/BulkImportExport";
import EditableCombobox from "../../components/EditableCombobox";
import SearchSelect from "../../components/SearchSelect";
import StatusFilterBar from "../../components/StatusFilterBar";
import { useAuth } from "../../context/AuthContext";
import { useServerTable } from "../../hooks/useServerTable";
import { useStatusCounts } from "../../hooks/useStatusCounts";
import { downloadBlob } from "../../utils/downloadBlob";
import {
  units as api,
  blocks as blocksApi,
  unitTypes as unitTypesApi,
  type Unit,
  type Block,
  type UnitType,
  type ApiError,
  type CsvImportResult,
} from "../../services/api";

const UNIT_STATUS_FILTERS = ["all", "vacant", "occupied"] as const;
type UnitStatusFilter = (typeof UNIT_STATUS_FILTERS)[number];

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};
const actionBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
  color: "var(--nx-text-muted)",
  display: "inline-flex",
  alignItems: "center",
};

const INIT = {
  unitNumber: "",
  blockId: "",
  unitTypeId: "",
  floor: "",
  areaSqft: "",
  status: "vacant",
};

const statusColor = (s: string) =>
  s === "occupied"
    ? { bg: "rgba(26,108,246,0.1)", text: "#1A6CF6" }
    : { bg: "rgba(16,185,129,0.1)", text: "#10B981" };

const UnitsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [statusFilter, setStatusFilter] = useState<UnitStatusFilter>("all");

  const {
    rows,
    totalElements,
    totalPages,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortField,
    sortDir,
    setSort,
    sort,
    q,
    setQ,
    refetch,
  } = useServerTable<Unit>(
    (params) =>
      societyId
        ? api.list(societyId, {
            ...params,
            status: statusFilter === "all" ? undefined : statusFilter,
          })
        : Promise.resolve({ content: [], totalElements: 0, totalPages: 0 }),
    [societyId, statusFilter],
    "unitNumber,asc",
  );

  const {
    counts: statusCounts,
    loading: statusCountsLoading,
    refetch: refetchStatusCounts,
  } = useStatusCounts(
    UNIT_STATUS_FILTERS,
    (key) =>
      societyId
        ? api
            .list(societyId, {
              page: 0,
              size: 1,
              status: key === "all" ? undefined : key,
            })
            .then((r) => r.totalElements)
        : Promise.resolve(0),
    [societyId],
  );

  const [blocks, setBlocks] = useState<Block[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [blocksError, setBlocksError] = useState(false);
  const [unitTypeOptions, setUnitTypeOptions] = useState<UnitType[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmUnit, setConfirmUnit] = useState<Unit | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(
    null,
  );

  const loadBlocks = () => {
    if (!societyId) return;
    setBlocksLoading(true);
    setBlocksError(false);
    blocksApi
      .list(societyId)
      .then(setBlocks)
      .catch(() => setBlocksError(true))
      .finally(() => setBlocksLoading(false));
  };

  useEffect(() => {
    if (!showForm || !societyId || blocks.length > 0) return;
    loadBlocks();
  }, [showForm, societyId]);

  useEffect(() => {
    if (!showForm || !societyId || unitTypeOptions.length > 0) return;
    unitTypesApi
      .list(societyId)
      .then(setUnitTypeOptions)
      .catch(() => {});
  }, [showForm, societyId]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const openAdd = () => {
    setEditing(null);
    setFormError("");
    setForm(INIT);
    setShowForm(true);
  };

  const openEdit = (u: Unit) => {
    setEditing(u);
    setFormError("");
    setForm({
      unitNumber: u.unitNumber,
      blockId: u.blockId ?? "",
      unitTypeId: u.unitTypeId ?? "",
      floor: u.floor != null ? String(u.floor) : "",
      areaSqft: u.areaSqft != null ? String(u.areaSqft) : "",
      status: u.status,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(INIT);
    setFormError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true);
    setFormError("");
    const payload = {
      unitNumber: form.unitNumber,
      blockId: form.blockId || undefined,
      unitTypeId: form.unitTypeId || undefined,
      floor: form.floor ? Number(form.floor) : undefined,
      areaSqft: form.areaSqft ? Number(form.areaSqft) : undefined,
      status: form.status as Unit["status"],
    };
    try {
      if (editing) {
        await api.update(societyId, editing.id, payload);
      } else {
        await api.create(societyId, payload);
      }
      refetch();
      refetchStatusCounts();
      closeForm();
    } catch (err) {
      setFormError(
        (err as ApiError).message || "Failed to save unit. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (u: Unit) => {
    setConfirmUnit(u);
    setDeleteError("");
  };
  const handleBulkDelete = () => {
    if (selectedUnitIds.length === 0) return;
    setDeleteError("");
    setConfirmBulkDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!societyId || !confirmUnit) return;
    setDeletingId(confirmUnit.id);
    try {
      await api.delete(societyId, confirmUnit.id);
      refetch();
      refetchStatusCounts();
      setConfirmUnit(null);
      setSelectedUnitIds([]);
    } catch {
      setDeleteError("Failed to delete unit. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const confirmBulkDelete = async () => {
    if (!societyId || selectedUnitIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await api.bulkDelete(societyId, selectedUnitIds);
      refetch();
      refetchStatusCounts();
      setSelectedUnitIds([]);
      setConfirmBulkDeleteOpen(false);
    } catch {
      setDeleteError("Failed to delete selected units. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleImport = async (file: File) => {
    if (!societyId) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await api.importCsv(societyId, file);
      setImportResult(result);
      refetch();
      refetchStatusCounts();
      setSelectedUnitIds([]);
    } catch (err) {
      setImportResult({
        created: 0,
        failed: 1,
        errors: [(err as ApiError).message || "Import failed"],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    if (!societyId) return;
    setExporting(true);
    try {
      const blob = await api.exportCsv(societyId, sort);
      downloadBlob(blob, "units-export.csv");
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  const unitNumberOptions = useMemo(() => {
    const filtered = form.blockId
      ? rows.filter((u) => u.blockId === form.blockId)
      : rows;
    const unique = Array.from(new Set(filtered.map((u) => u.unitNumber)));
    return unique.map((v) => ({ value: v, label: v }));
  }, [rows, form.blockId]);

  const selectedBlock = blocks.find((b) => b.id === form.blockId);
  const floorOptions = useMemo(() => {
    if (!selectedBlock?.floorCount) return [];
    return Array.from({ length: selectedBlock.floorCount }, (_, i) => {
      const v = String(i + 1);
      return { value: v, label: v };
    });
  }, [selectedBlock]);

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--nx-text-primary)",
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            Units
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {totalElements} unit{totalElements !== 1 ? "s" : ""} registered
          </p>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <BulkImportExport
              onImport={handleImport}
              onExport={handleExport}
              importing={importing}
              exporting={exporting}
              acceptExcel
            />
            <button
              onClick={openAdd}
              className="nx-btn nx-btn-primary"
              style={{ gap: 6 }}
            >
              <Plus size={14} /> Add Unit
            </button>
          </div>
          <span style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>
            CSV or Excel columns: unitNumber, block, floor, areaSqft, unitType.
            New units start vacant; status becomes occupied automatically once a
            resident is assigned.
          </span>
        </div>
      </div>

      {importResult && (
        <div
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            fontSize: 13,
            marginBottom: 16,
            background:
              importResult.failed > 0
                ? "rgba(239,68,68,0.08)"
                : "rgba(34,197,94,0.08)",
            border: `1px solid ${importResult.failed > 0 ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`,
            color: importResult.failed > 0 ? "#EF4444" : "#16A34A",
          }}
        >
          Imported {importResult.created} units
          {importResult.failed > 0
            ? `, ${importResult.failed} failed: ${importResult.errors.join("; ")}`
            : "."}
        </div>
      )}

      <StatusFilterBar
        options={UNIT_STATUS_FILTERS.map((f) => ({
          key: f,
          label: f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1),
        }))}
        active={statusFilter}
        onChange={setStatusFilter}
        counts={statusCounts}
        countsLoading={statusCountsLoading}
      />

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            {
              key: "unit",
              header: "Unit Number",
              cellStyle: { fontWeight: 500 },
              sortable: true,
              sortKey: "unitNumber",
              render: (u: Unit) => u.unitNumber,
            },
            {
              key: "block",
              header: "Block",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "b.name",
              render: (u: Unit) => u.blockName || "—",
            },
            {
              key: "unitType",
              header: "Flat Type",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "ut.name",
              render: (u: Unit) => u.unitTypeName || "—",
            },
            {
              key: "floor",
              header: "Floor",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "floor",
              render: (u: Unit) => (u.floor != null ? `Floor ${u.floor}` : "—"),
            },
            {
              key: "area",
              header: "Area (sq ft)",
              cellStyle: { color: "var(--nx-text-secondary)" },
              sortable: true,
              sortKey: "areaSqft",
              render: (u: Unit) =>
                u.areaSqft != null ? u.areaSqft.toLocaleString() : "—",
            },
            {
              key: "occupant",
              header: "Occupant",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (u: Unit) => {
                if (!u.occupants || u.occupants.length === 0) return "—";
                // Show just the primary occupant (owner, else tenant, else whoever's
                // first) so a unit with several family members doesn't stretch its row
                // taller than every other row in the grid — the rest collapse into a
                // "+N more" chip with the full list on hover.
                const primary =
                  u.occupants.find((o) => o.type === "owner") ??
                  u.occupants.find((o) => o.type === "tenant") ??
                  u.occupants[0];
                const rest = u.occupants.filter((o) => o.id !== primary.id);
                return (
                  <div>
                    <div style={{ fontWeight: 500, color: "var(--nx-text-primary)" }}>
                      {primary.fullName}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>
                      {[primary.phone, primary.email].filter(Boolean).join(" · ") || "—"}
                    </div>
                    {rest.length > 0 && (
                      <span
                        title={rest.map((o) => o.fullName).join(", ")}
                        style={{
                          display: "inline-block",
                          marginTop: 2,
                          fontSize: 10,
                          fontWeight: 500,
                          color: "var(--nx-text-muted)",
                          background: "rgba(148,163,184,0.1)",
                          padding: "1px 6px",
                          borderRadius: 99,
                        }}
                      >
                        +{rest.length} more
                      </span>
                    )}
                  </div>
                );
              },
            },
            {
              key: "status",
              header: "Status",
              sortable: true,
              sortKey: "status",
              render: (u: Unit) => {
                const sc = statusColor(u.status);
                return (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 500,
                      background: sc.bg,
                      color: sc.text,
                    }}
                  >
                    {u.status.charAt(0).toUpperCase() + u.status.slice(1)}
                  </span>
                );
              },
            },
            {
              key: "actions",
              header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (u: Unit) => (
                <>
                  <button
                    onClick={() => openEdit(u)}
                    style={actionBtn}
                    title="Edit unit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(u)}
                    disabled={deletingId === u.id}
                    style={{
                      ...actionBtn,
                      color: "#EF4444",
                      opacity: deletingId === u.id ? 0.4 : 1,
                    }}
                    title="Delete unit"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={rows}
          rowKey={(u) => u.id}
          loading={loading}
          emptyIcon={
            <LayoutGrid size={32} style={{ color: "var(--nx-text-muted)" }} />
          }
          emptyText="No units yet. Add your first unit."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Search units…"
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedRowIds={selectedUnitIds}
          onSelectionChange={setSelectedUnitIds}
          toolbarActions={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {selectedUnitIds.length > 0 && (
                <span
                  style={{ fontSize: 12, color: "var(--nx-text-secondary)" }}
                >
                  {selectedUnitIds.length} selected
                </span>
              )}
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={selectedUnitIds.length === 0}
                className="nx-btn nx-btn-outline"
                style={{ gap: 6 }}
              >
                <Trash2 size={14} /> Delete selected
              </button>
            </div>
          }
        />
      </div>

      <ConfirmDialog
        open={confirmUnit !== null}
        variant="danger"
        title={`Delete unit "${confirmUnit?.unitNumber}"?`}
        message="This unit will be permanently removed. This action cannot be undone."
        confirmLabel="Delete Unit"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmUnit(null);
          setDeleteError("");
        }}
      />
      <ConfirmDialog
        open={confirmBulkDeleteOpen}
        variant="danger"
        title="Delete selected units?"
        message={`This will permanently remove ${selectedUnitIds.length} selected unit${selectedUnitIds.length !== 1 ? "s" : ""}. This action cannot be undone.`}
        confirmLabel="Delete Units"
        isLoading={bulkDeleting}
        error={deleteError}
        onConfirm={confirmBulkDelete}
        onCancel={() => {
          setConfirmBulkDeleteOpen(false);
          setDeleteError("");
        }}
      />

      {showForm && (
        <Modal
          title={editing ? "Edit Unit" : "Add Unit"}
          submitLabel={editing ? "Save Changes" : "Add Unit"}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Unit Number *</label>
              <EditableCombobox
                name="unitNumber"
                value={form.unitNumber}
                onChange={(v) => setForm((p) => ({ ...p, unitNumber: v }))}
                options={unitNumberOptions}
                placeholder="e.g. A-101"
              />
            </div>
            <div>
              <label style={lbl}>Block</label>
              {blocksLoading ? (
                <div style={{ ...inp, color: "var(--nx-text-muted)" }}>
                  Loading blocks…
                </div>
              ) : blocksError ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#EF4444" }}>
                    Failed to load.
                  </span>
                  <button
                    type="button"
                    onClick={loadBlocks}
                    style={{
                      fontSize: 12,
                      color: "var(--nx-blue)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <SearchSelect
                  value={form.blockId}
                  onChange={(v) => setForm((p) => ({ ...p, blockId: v }))}
                  options={blocks.map((b) => ({ value: b.id, label: b.name }))}
                  placeholder={
                    blocks.length === 0
                      ? "No blocks available"
                      : "Search blocks…"
                  }
                  disabled={blocks.length === 0}
                />
              )}
            </div>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Flat Type</label>
              <SearchSelect
                value={form.unitTypeId}
                onChange={(v) => setForm((p) => ({ ...p, unitTypeId: v }))}
                options={unitTypeOptions.map((ut) => ({
                  value: ut.id,
                  label: ut.name,
                }))}
                placeholder={
                  unitTypeOptions.length === 0
                    ? "No flat types configured"
                    : "Search flat types…"
                }
                disabled={unitTypeOptions.length === 0}
              />
            </div>
            <div>
              <label style={lbl}>Floor</label>
              <EditableCombobox
                name="floor"
                value={form.floor}
                onChange={(v) => setForm((p) => ({ ...p, floor: v }))}
                options={floorOptions}
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <label style={lbl}>Area (sq ft)</label>
              <input
                type="number"
                name="areaSqft"
                value={form.areaSqft}
                onChange={change}
                placeholder="e.g. 1200"
                min="0"
                style={inp}
              />
            </div>
          </div>

          <div>
            <label style={lbl}>Status</label>
            <select
              name="status"
              value={form.status}
              onChange={change}
              style={inp}
            >
              <option value="vacant">Vacant</option>
              <option value="occupied">Occupied</option>
            </select>
          </div>

          {editing && (
            <div>
              <label style={lbl}>Occupant</label>
              {editing.occupants && editing.occupants.length > 0 ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {editing.occupants.map((o) => (
                    <div
                      key={o.id}
                      style={{
                        padding: "8px 12px",
                        background: "var(--nx-surface-3)",
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "var(--nx-text-primary)",
                        }}
                      >
                        {o.fullName}
                        {o.type && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 400,
                              color: "var(--nx-text-muted)",
                            }}
                          >
                            {" "}
                            · {o.type.charAt(0).toUpperCase() + o.type.slice(1)}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--nx-text-secondary)",
                        }}
                      >
                        {[o.phone, o.email].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ ...inp, color: "var(--nx-text-muted)" }}>
                  No resident linked to this unit yet — add one from the
                  Residents page.
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </PageLayout>
  );
};

export default UnitsPage;
